const SELECTED_REGISTER_KEY = 'pos_selected_register_uuid'
const ACTIVE_SESSION_KEY = 'pos_active_register_session_uuid'
const SESSION_DRAWER_FA_KEY = 'pos_active_session_drawer_fa'

export type SessionDrawerFaCache = {
  branchId: string
  registerId: string
  sessionId: string
  drawerFaId: string
}

/** Explicit financial context only; it is intentionally independent of device identity. */
export function getSelectedPosRegisterId(): string | null {
  return typeof localStorage === 'undefined' ? null : localStorage.getItem(SELECTED_REGISTER_KEY)
}

export function setSelectedPosRegisterId(id: string | null): void {
  if (typeof localStorage === 'undefined') return
  if (id) localStorage.setItem(SELECTED_REGISTER_KEY, id)
  else {
    localStorage.removeItem(SELECTED_REGISTER_KEY)
    clearCachedSessionDrawerFa()
  }
}

export function getActiveRegisterSessionId(): string | null {
  return typeof localStorage === 'undefined' ? null : localStorage.getItem(ACTIVE_SESSION_KEY)
}

export function setActiveRegisterSessionId(id: string | null): void {
  if (typeof localStorage === 'undefined') return
  if (id) localStorage.setItem(ACTIVE_SESSION_KEY, id)
  else {
    localStorage.removeItem(ACTIVE_SESSION_KEY)
    clearCachedSessionDrawerFa()
  }
}

export function setCachedSessionDrawerFa(input: SessionDrawerFaCache): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(SESSION_DRAWER_FA_KEY, JSON.stringify(input))
}

export function clearCachedSessionDrawerFa(): void {
  if (typeof localStorage === 'undefined') return
  localStorage.removeItem(SESSION_DRAWER_FA_KEY)
}

function readCachedSessionDrawerFa(): SessionDrawerFaCache | null {
  if (typeof localStorage === 'undefined') return null
  const raw = localStorage.getItem(SESSION_DRAWER_FA_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as SessionDrawerFaCache
    if (
      !parsed?.branchId
      || !parsed?.registerId
      || !parsed?.sessionId
      || !parsed?.drawerFaId
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

/** Returns drawer FA id only when cache matches the current branch/register/session context. */
export function getCachedSessionDrawerFaId(input: {
  branchId?: string | null
  registerId?: string | null
  sessionId?: string | null
} = {}): string | null {
  const cached = readCachedSessionDrawerFa()
  if (!cached) return null

  const branchId = input.branchId ?? null
  const registerId = input.registerId ?? getSelectedPosRegisterId()
  const sessionId = input.sessionId ?? getActiveRegisterSessionId()

  if (!branchId || !registerId || !sessionId) return null
  if (
    cached.branchId !== String(branchId)
    || cached.registerId !== String(registerId)
    || cached.sessionId !== String(sessionId)
  ) {
    return null
  }

  return cached.drawerFaId
}

export function resolveSessionDrawerFinancialAccountId(input: {
  drawerFinancialAccountId?: string | null
  cashDrawerFinancialAccountId?: string | null
  registerCashDrawerFinancialAccountId?: string | null
}): string | null {
  const candidates = [
    input.drawerFinancialAccountId,
    input.cashDrawerFinancialAccountId,
    input.registerCashDrawerFinancialAccountId,
  ]
  for (const candidate of candidates) {
    if (candidate != null && String(candidate).trim() !== '') {
      return String(candidate)
    }
  }
  return null
}

export type RegisterMoneyContextOverride = {
  pos_register_id?: string | null
  register_session_id?: string | null
}

/** Stamp Class A money payloads with the original session captured at write time. */
export function registerMoneyContextFields(override?: RegisterMoneyContextOverride): {
  pos_register_id?: string
  register_session_id?: string
} {
  const registerId = override && 'pos_register_id' in override
    ? override.pos_register_id
    : getSelectedPosRegisterId()
  const sessionId = override && 'register_session_id' in override
    ? override.register_session_id
    : getActiveRegisterSessionId()
  return {
    ...(registerId ? { pos_register_id: registerId } : {}),
    ...(sessionId ? { register_session_id: sessionId } : {}),
  }
}

export function registerMoneyContextFromSession(session?: {
  uuid?: string | null
  register?: { uuid?: string | null } | null
} | null): {
  pos_register_id?: string
  register_session_id?: string
} {
  if (session?.uuid) {
    return registerMoneyContextFields({
      register_session_id: session.uuid,
      pos_register_id: session.register?.uuid ?? getSelectedPosRegisterId(),
    })
  }
  return registerMoneyContextFields()
}

export function canActAsRegisterSupervisor(
  hasPermission: (name: string) => boolean,
  isSuperAdmin = false,
): boolean {
  return isSuperAdmin
    || hasPermission('manage_shifts')
    || hasPermission('access_admin_routes')
    || hasPermission('force_close_register_session')
}

export function canSelectAnyRegisterDrawer(
  hasPermission: (name: string) => boolean,
  isSuperAdmin = false,
): boolean {
  return canActAsRegisterSupervisor(hasPermission, isSuperAdmin)
}

export function isCashDrawerPaymentSource<T extends {
  simple_type?: string | null
  account_type?: string | null
}>(source?: T | null): source is T {
  return source != null && (source.simple_type === 'cash_drawer' || source.account_type === 'cash_drawer')
}

export function assertActiveRegisterSessionForSale(branchRegisterMode?: string | null): void {
  if (branchRegisterMode !== 'multi_register') return
  if (!getActiveRegisterSessionId() || !getSelectedPosRegisterId()) {
    throw new Error('register_session_required')
  }
}

export function assertActiveRegisterSessionForCash(branchRegisterMode?: string | null): void {
  assertActiveRegisterSessionForSale(branchRegisterMode)
}
